import { useQuery } from "@tanstack/react-query";
import { type Dayjs } from "dayjs";
import keyBy from "lodash.keyby";

import {
  ExerciseRecordingStatus,
  type ExerciseRecordingWithExerciseInfo,
} from "@fitness-app/data-models/entities/ExerciseRecording";
import { fetchAllPostgrestRows } from "@fitness-app/utils/src/helpers/fetchAllPostgrestRows";

import { traineeKeys } from "~/modules/Trainee/TraineeProfile/scope";
import { supabase } from "~/store/initializeStore";

const VISIBLE_STATUSES = [
  ExerciseRecordingStatus.SCHEDULED,
  ExerciseRecordingStatus.RECORDED,
  ExerciseRecordingStatus.RATED,
];

const RECORDING_SELECT =
  "*, exercise:parentExerciseId(name), plan:programId(name), feedback:feedbackId(createdAt,content,files)";

export type ExerciseRecordingWithManualWorkoutInfo = ExerciseRecordingWithExerciseInfo & {
  isManualWorkout?: boolean;
};

export const EXERCISE_RECORDINGS_KEY = "exerciseRecordings";

export const useTraineeExerciseRecordings = (traineeId: string) => {
  // Under the card prefix, so closing a builder refreshes it with the rest of the card. It used to be
  // `["exercise_recordings", traineeId]`, the very key the calendar's hook below also used, so whichever
  // mounted first served both. `useUpdateExerciseRecording` invalidates it by the second key segment.
  const { data, isLoading } = useQuery([...traineeKeys.all(traineeId), EXERCISE_RECORDINGS_KEY], {
    queryFn: async () => {
      const query = supabase
        .from("exercise_recording")
        .select(RECORDING_SELECT)
        .eq("traineeId", traineeId)
        .in("status", VISIBLE_STATUSES)
        .order("scheduledAt", { ascending: false })
        .limit(100);

      const { error, data } = await query.returns<ExerciseRecordingWithExerciseInfo[]>();
      if (error) {
        return [];
      }

      const scheduledWorkoutIds = Array.from(
        new Set(
          data
            .filter((recording) => recording.status === ExerciseRecordingStatus.SCHEDULED && !recording.url)
            .map((recording) => recording.workoutId),
        ),
      );

      if (!scheduledWorkoutIds.length) {
        return data;
      }

      const { data: manualWorkouts } = await supabase
        .from("client_workout_activity")
        .select("workoutId")
        .eq("traineeId", traineeId)
        .in("workoutId", scheduledWorkoutIds);

      const manualWorkoutIds = new Set(manualWorkouts?.map((workout) => workout.workoutId) ?? []);

      return data.map((recording): ExerciseRecordingWithManualWorkoutInfo => ({
        ...recording,
        isManualWorkout: manualWorkoutIds.has(recording.workoutId),
      }));
    },
  });

  return {
    data,
    isLoading,
  };
};

/**
 * Recordings for everything the calendar can show, scoped by date rather than by program.
 *
 * This used to filter on the selected program's id, which quietly hid recordings the client really
 * does owe: training programs run in 4-week blocks while the grid spans a month plus the adjacent
 * days, so a challenge workout landing in the next block had its row in the database and no camera
 * icon on screen (TRE-1791). Padding by a month each side mirrors `fetchTraineeActivitiesForRange`,
 * which is what puts those out-of-month days on the grid in the first place.
 */
export const useTraineeExerciseRecordingsForRange = (traineeId: string, range: { start: Dayjs; end: Dayjs }) => {
  const from = range.start.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
  const to = range.end.add(1, "month").endOf("month").format("YYYY-MM-DD");

  const { data, isLoading } = useQuery(["exercise_recordings", "trainee-range", traineeId, from, to], {
    enabled: Boolean(traineeId),
    queryFn: async () => {
      try {
        // Paged rather than capped: a month-long challenge alone adds a dozen recordings, and a
        // silently truncated response would look exactly like a missing recording.
        return await fetchAllPostgrestRows<ExerciseRecordingWithExerciseInfo>(
          ({ from: rangeFrom, to: rangeTo, withCount }) =>
            supabase
              .from("exercise_recording")
              .select(RECORDING_SELECT, withCount ? { count: "exact" } : undefined)
              .eq("traineeId", traineeId)
              .gte("scheduledAt", from)
              .lte("scheduledAt", to)
              .in("status", VISIBLE_STATUSES)
              .order("id", { ascending: true })
              .range(rangeFrom, rangeTo)
              .returns<ExerciseRecordingWithExerciseInfo[]>(),
        );
      } catch {
        return [];
      }
    },
  });

  return {
    data,
    isLoading,
    byWorkoutExerciseId: data ? keyBy(data, (recording) => `${recording.workoutId}:${recording.parentExerciseId}`) : {},
  };
};
